import { FORMER_MEMBER, joinWithAnd, type ChatEvent } from "@omnabase/shared";
import { formatRelative, type RailDot } from "@omnabase/ui";

/** The words of the discussion's one condensed item in the ticket's conversation and the band's Discussion dot (the discussion polish round,
 *  2026-11-02; brief docs/briefs/discussion-polish-hours-editor-round.md rules 2 and 6, the red team's finding 9). Pure. In plain words:
 *  - only real lines count (not the room's own lines, not deleted ones); without one there is no item;
 *  - "4 lines · Mia, Rikard and you · 2 h ago": the count, the writers by first name newest first ("you" last, more than three colleagues
 *    "Anna, Yigit, Mike, 2 more and you"), the newest line's age;
 *  - the newest line's words on one line, at most 120 characters cut at a space with "…", or "Mia mentioned you" while the reader has an
 *    unread mention (naming who wrote the newest unread line that mentions them);
 *  - "2 new" is the server's unread count (followers only). */
type Member = { userId: string; name: string };
type Room = { unread: number; mentions: number; readSeq: number };
export type DiscussionDigestWords = { at: string; count: number; meta: string; text: string; fresh: number; mentionedBy: string | null };
export type DigestContext = { members: readonly Member[]; formerAuthors?: readonly { id: string; name: string }[]; me: string; room: Room | null; now: number };

const MAX_TEXT = 120;
const MAX_COLLEAGUES = 3;
const firstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;
const byTime = (a: ChatEvent, b: ChatEvent) => Date.parse(a.createdAt) - Date.parse(b.createdAt) || a.seq - b.seq;

export function discussionDigestOf(events: readonly ChatEvent[], c: DigestContext): DiscussionDigestWords | null {
  const lines = events.filter((e) => e.type === "message" && !e.redactedAt).sort(byTime);
  const newest = lines[lines.length - 1];
  if (!newest) return null;
  const mentionedBy = mentionerOf(lines, c);
  const meta = `${lines.length} ${lines.length === 1 ? "line" : "lines"} · ${authorsOf(lines, c)} · ${formatRelative(newest.createdAt, c.now)}`;
  return { at: newest.createdAt, count: lines.length, meta, text: mentionedBy ? `${mentionedBy} mentioned you` : shortText(newest.text), fresh: c.room?.unread ?? 0, mentionedBy };
}

/** the writers by first name, newest first, each once; "you" last; more than three colleagues end in "<n> more" */
function authorsOf(lines: readonly ChatEvent[], c: DigestContext): string {
  const seen = new Set<string>(); let you = false;
  for (const e of [...lines].reverse()) {
    const id = e.senderId ?? "";
    if (id === c.me) you = true; else seen.add(id);
  }
  const names = [...seen].map((id) => firstNameOf(id, c));
  const shown = names.length > MAX_COLLEAGUES ? [...names.slice(0, MAX_COLLEAGUES), `${names.length - MAX_COLLEAGUES} more`] : names;
  return joinWithAnd([...shown, ...(you ? ["you"] : [])]);
}
/** a writer's first name (a removed colleague's from the name their line was written with); a deleted account reads FORMER_MEMBER */
function firstNameOf(id: string, c: DigestContext): string {
  const name = c.members.find((m) => m.userId === id)?.name ?? c.formerAuthors?.find((f) => f.id === id)?.name;
  return name ? firstName(name) : FORMER_MEMBER;
}

/** the first name of whoever wrote the newest unread line that mentions the reader, while the room counts an unread mention */
function mentionerOf(lines: readonly ChatEvent[], c: DigestContext): string | null {
  if (!c.room || c.room.mentions <= 0) return null;
  const readSeq = c.room.readSeq;
  const line = [...lines].reverse().find((e) => e.seq > readSeq && e.senderId !== c.me && e.mentions.includes(c.me));
  return line ? firstNameOf(line.senderId ?? "", c) : null;
}

/** the words on one line, at most 120 characters, cut at the last space before that with "…" */
function shortText(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= MAX_TEXT) return flat;
  const cut = flat.slice(0, MAX_TEXT), space = cut.lastIndexOf(" ");
  return `${(space > 0 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/** the band's Discussion dot, the rail's rule: the accent for an unread mention, grey for other unread lines, none when all is read */
export function discussionDot(room: Pick<Room, "unread" | "mentions"> | null): RailDot {
  if (room && room.mentions > 0) return true;
  if (room && room.unread > 0) return "quiet";
  return false;
}
